import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Item, ItemFilter, ItemFolder, ItemLabel, ItemPagination, Items } from 'app/modules/admin/apps/file-manager/file-manager.types';
import { BehaviorSubject, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { FuseAlertComponent, FuseAlertService } from '@fuse/components/alert';
import { FuseHighlightComponent } from '@fuse/components/highlight';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from "@angular/material/icon";
import { iconTypes } from 'app/modules/admin/apps/file-manager/file-manager.constants';
import { Asset } from 'app/models/document';
import { DocumentService } from 'app/services/document.service';
import { Util } from 'app/models/util';
import { EventService } from 'app/services/event.service';
import { HttpClientModule } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PlatformLocation } from '@angular/common';
import { environment } from 'environments/environment';
import { LpmLabelPipe } from '@fuse/pipes/lpm-label.pipe';

/**
 * The file-manager.service.ts is a service class in TypeScript that handles interactions with the server 
 * for file management operations. It encapsulates the logic for making HTTP requests to the server, 
 * processing responses, and managing data related to files.
 * 
 * This service provides methods for various operations such as retrieving file data, 
 * creating new files, updating existing files, and deleting files. It acts as a bridge between the 
 * client-side application and the server, abstracting away the complexities of server communication 
 * and providing a clean, easy-to-use interface for file management.
 */

@Injectable({ providedIn: 'root' })
export class FileManagerService {
    // Private
    private _item: BehaviorSubject<Item | null> = new BehaviorSubject(null);
    private _items: BehaviorSubject<Items | null> = new BehaviorSubject(null);
    private _labels: BehaviorSubject<ItemLabel[]> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<ItemPagination | null> = new BehaviorSubject(null);
    private _filters: BehaviorSubject<ItemFilter[]> = new BehaviorSubject(null);

    iconTypes: any = iconTypes;

    private _viewMode: 'grid' | 'cards' = 'grid';
    private _alertDuration: number = 4000;

    public isLoading: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _fuseAlertService: FuseAlertService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _formBuilder: UntypedFormBuilder,
        private domSanitizer: DomSanitizer,
        private matIconRegistry: MatIconRegistry,
        private docService: DocumentService,
        private util: Util,
        private toastr: ToastrService,
        private eventSer: EventService,
        private platformLocation: PlatformLocation,
        private labelPipe: LpmLabelPipe
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for items
     */
    get items$(): Observable<Items> {
        return this._items.asObservable();
    }

    /**
     * Getter for item
     */
    get item$(): Observable<Item> {
        return this._item.asObservable();

    }

    /**
     * Getter for labels
     */
    get labels$(): Observable<ItemLabel[]> {
        return this._labels.asObservable();
    }

    /**
         * Getter for filters
         */
    get filters$(): Observable<ItemFilter[]> {
        return this._filters.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<ItemPagination> {
        return this._pagination.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Retrieves the items from the file manager.
     * @param folderId The ID of the folder to retrieve items from. If null, retrieves items from the root folder.
     * @returns An Observable that emits an array of items.
     */
    getItems(folderId: string | null = null): Observable<Item[]> {
        return this._httpClient.get<Items>('api/apps/file-manager', { params: { folderId } }).pipe(
            tap((response: any) => {
                this._items.next(response);
            }),
        );
    }

    /**
     * Retrieves an item by its ID.
     * @param id - The ID of the item to retrieve.
     * @returns An Observable that emits the retrieved item.
     */
    getItemById(id: string): Observable<Item> {
        return this._items.pipe(
            take(1),
            map((items) => {
                // Find within the folders and files
                const item = [...items.folders, ...items.files].find(value => value.id === id) || null;

                // Update the item
                this._item.next(item);

                // Return the item
                return item;
            }),
            switchMap((item) => {
                if (!item) {
                    return throwError('Could not found the item with id of ' + id + '!');
                }

                return of(item);
            }),
        );
    }

    /**
     * Deletes a file or folder from the trash.
     * If an id is provided, it deletes the specific file or folder with that id.
     * If no id is provided, it deletes all files and folders from the trash.
     * @param {number} id - The id of the file or folder to be deleted. Optional.
     */
    emptyTrash(id: string) {
        this.docService.deleteRecord('trash/empty').then((res) => {
            this.toastr.error(id != null ? "Item deleted" : "All items deleted", "DELETED");
            this.eventSer.getTrashData.emit("trash");
            this.eventSer.checkUnreadMessages.emit("Trash");
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Moves a folder or file to the trash.
     * 
     * @param {number} folderId - The ID of the folder to be moved to the trash.
     * @param {string} type - The type of the item to be moved ('folder' or 'file').
     * @returns {Promise<void>} - A promise that resolves when the item is successfully moved to the trash.
     */
    trash(ids: string[], type) {
        const trashedTypeTitle = ids.length > 1 ? 'Files' : 'File'
        const label = this.labelPipe.transform('Trash', 'deleteLabelText');
        const trashLocation = this.labelPipe.transform('Trash', 'trashLocationLabel');
        this.docService.trash(ids).then((res) => {
            this.toastr.warning(type === 'file' ? `${trashedTypeTitle} moved to ${trashLocation}` : `Folder moved to ${trashLocation}`, label);
            this.eventSer.getData.emit(true);
            this.eventSer.checkUnreadMessages.emit("Trash");

        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Moves the specified item to the trash.
     * 
     * @param item - The item to be trashed.
     */
    trashItem(item: any): void {
        console.log("trashItem entered!");
        // Write your logic to delete the item

        // Show the success message
        this._fuseAlertService.show('alert_trash');

        // Hide the alert after 3 seconds
        setTimeout(() => {
            this._fuseAlertService.dismiss('alert_trash');
        }, this._alertDuration);
    }

    /**
     * Moves multiple items to the trash.
     * @param items - The items to be moved to the trash.
     */
    trashItem_multi(ids: string[]): void {
        this.docService.trash(ids).then((res) => {
            this.toastr.warning(`Files moved to ${this.labelPipe.transform('Trash', 'trashLocationLabel')}`);
            this.eventSer.checkUnreadMessages.emit("Trash");
            this.eventSer.getData.emit(true);
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Deletes multiple items.
     * @param items - The items to be deleted.
     */
    deleteItem_multi(ids): void {
        this.docService.delete_multi(ids).then((res)=>{
            this.toastr.error("Items deleted successfully", "DELETED");
            this.eventSer.getTrashData.emit("trash");
        }).catch((error)=>{
            console.log(error);
        })
    }

    /**
     * Deletes an item.
     * @param item - The item to be deleted.
     */
    deleteItem(id): void {
        this.docService.deleteRecord(id).then((res)=>{
            this.toastr.error("Deleted successfully", "DELETED");
            this.eventSer.getTrashData.emit("trash");
        }).catch((error)=>{
            console.log(error);
        })
    }

    /**
     * Downloads the specified item.
     * 
     * @param item - The item to download.
     */
    downloadItem(item: any): void {
        // Write your logic to download the item

        // Show the success message
        this._fuseAlertService.show('alert_download');

        // Hide the alert after 3 seconds
        setTimeout(() => {
            this._fuseAlertService.dismiss('alert_download');
        }, this._alertDuration);
    }

    /**
     * Restores the specified items.
     * 
     * @param items - The items to be restored.
     */
    restoreItems(ids: string[]): void {
        this.docService.restore(ids).then(res => {
            this.toastr.success(`Item successfully recovered from ${this.labelPipe.transform('Trash', 'trashLocationLabel')}`, "Restored");
            this.eventSer.getTrashData.emit(true);
            this.eventSer.checkUnreadMessages.emit("Recovered");
        }).catch(error => {
            console.log(error)
        })
    }

    /**
     * Restores an item.
     * 
     * @param item - The item to be restored.
     */
    restoreItem(item: any): void {
        // Write your logic to download the item

        // Show the success message
        this._fuseAlertService.show('alert_restore');

        // Hide the alert after 3 seconds
        setTimeout(() => {
            this._fuseAlertService.dismiss('alert_restore');
        }, this._alertDuration);
    }

    /**
     * Downloads the specified assets.
     * If multiple assets are provided, it calls the server method to batch download the assets.
     * If only one asset is provided, it calls the downloadFile method to download the asset.
     * @param assets - The assets to be downloaded.
     */
    downloadAssets(assets: Asset[]): void {
        if (assets && assets.length == 0) {
            //TODO - call server method to batch download assets
        } else {
            this.downloadFile(assets[0]);
        }

        /*
        // Show the success message
        this._fuseAlertService.show(assets && assets.length > 1 ? 'alert_download_multi' : 'alert_download');

        // Hide the alert after 3 seconds
        setTimeout(() => {
            this._fuseAlertService.dismiss(assets && assets.length > 1 ? 'alert_download_multi' : 'alert_download');
        }, this._alertDuration);
        */
    }

    /**
    * Opens a dialog for downloading a file.
    * 
    * @param item - The item to download.
    */

    openDialog_download(item: any): void {
        //Check permissions
        // Open the dialog and save the reference of it
        const singleDownloadForm = this.util.singledownloadForm;
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(singleDownloadForm).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.downloadFile(item);
            }
        });
    }

    /**
     * Opens a dialog for downloading multiple assets.
     * 
     * @param assets - An array of assets to be downloaded.
     */
    openDialog_download_multi(assetIds:string[]): void {
        //Check permissions
        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.downloadForm).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            console.log("openDialog_download_multi - ", result);

            if (result === 'confirmed') {
                this.downloadZip(assetIds);
            }
        });
    }

    /**
     * Downloads a file.
     * 
     * @param file The file to be downloaded.
     */
    downloadFile(file: Asset) {
        this.isLoading = true;
        this.toastr.info("", "Downloading...");
        this.docService
            .openFile(file?.id)
            .then((res) => {
                const fileName = this.resolveFileName(file.name, file.extension);
                this.docService.processDownload(res, fileName);
                this.toastr.success("File download successful", file.name);
                this.eventSer.checkUnreadMessages.emit("File Download");
                this.eventSer.resetRowsSelection.emit()
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                console.log(err);
                this.eventSer.resetRowsSelection.emit()
            });

        return;
    }

    private resolveFileName(name: string, extension: string): string {
        if (!name) return 'download';
        if (!extension) return name;
    
        const ext = extension.startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
        const lastDot = name.lastIndexOf('.');
        const currentExt = lastDot !== -1 ? name.slice(lastDot).toLowerCase() : '';
    
        if (currentExt === ext) return name;                           // already correct
        if (!currentExt) return `${name}${ext}`;                      // no extension — append
        return `${name.slice(0, lastDot)}${ext}`;                     // wrong extension — replace
    }

    downloadZip(assetIds:string[]) {
        this.toastr.info("", "Downloading...");
        //this.isloadingState = true;
        this.docService
            .getAssetsZip(assetIds)
            .then((response) => {
                this.eventSer.checkUnreadMessages.emit("Download");
                this.eventSer.resetRowsSelection.emit()
                
                let timestamp = new Date().toLocaleString().replace(/[-:.]/g, '');
                const downloadTitle=timestamp+".zip"
                this.docService.processDownload(response, downloadTitle);
            })
            .catch((error) => {
                console.log(error);
                this.eventSer.resetRowsSelection.emit()
            });
    }

    /**
     * Get filters
     */
    getFilters(): Observable<any> {
        return this._httpClient.get<ItemFilter[]>('api/apps/file-manager/filters').pipe(
            tap((response: any) => {
                console.log("getFilters response: ", response);
                this._filters.next(response);
            }),
        );
    }

    /**
     * Get labels
     */
    getLabels(): Observable<any> {
        return this._httpClient.get<ItemLabel[]>('api/apps/file-manager/labels').pipe(
            tap((response: any) => {
                this._labels.next(response);
            }),
        );
    }

    /**
    * Add label
    *
    * @param label
    */
    addLabel(label: ItemLabel): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels => this._httpClient.post<ItemLabel>('api/apps/file-manager/label', { label }).pipe(
                map((newLabel) => {
                    // Update the labels with the new label
                    this._labels.next([...labels, newLabel]);

                    // Return the new label
                    return newLabel;
                }),
            )),
        );
    }

    /**
     * Update label
     *
     * @param id
     * @param label
     */
    updateLabel(id: string, label: ItemLabel): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels => this._httpClient.patch<ItemLabel>('api/apps/file-manager/label', {
                id,
                label,
            }).pipe(
                map((updatedLabel: any) => {
                    // Find the index of the updated label within the labels
                    const index = labels.findIndex(item => item.id === id);

                    // Update the label
                    labels[index] = updatedLabel;

                    // Update the labels
                    this._labels.next(labels);

                    // Return the updated label
                    return updatedLabel;
                }),
            )),
        );
    }

    /**
     * Delete label
     *
     * @param id
     */
    deleteLabel(id: string): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels => this._httpClient.delete('api/apps/file-manager/label', { params: { id } }).pipe(
                map((isDeleted: any) => {
                    // Find the index of the deleted label within the labels
                    const index = labels.findIndex(item => item.id === id);

                    // Delete the label
                    labels.splice(index, 1);

                    // Update the labels
                    this._labels.next(labels);

                    // Return the deleted status
                    return isDeleted;
                }),
            )),
        );
    }

    /**
     * Dismiss the alert via the service
     *
     * @param name
     */
    dismiss(name: string): void {
        this._fuseAlertService.dismiss(name);
    }

    /**
     * Show the alert via the service
     *
     * @param name
     */
    show(name: string): void {
        this._fuseAlertService.show(name);
    }

    registerIcons(): void {
        // List icon names without .svg extension
        iconTypes.forEach(iconName => {
            const iconPath = `${environment.assetsBaseUrl}/images/file-icons/${iconName}.svg`;
            this.matIconRegistry.addSvgIcon(
                iconName,
                this.domSanitizer.bypassSecurityTrustResourceUrl(iconPath)
            );
        });
    }

    public get viewMode(): 'grid' | 'cards' {
        return this._viewMode;
    }

    public set viewMode(mode: 'grid' | 'cards') {
        this._viewMode = mode;
    }

    /**
     * Opens a dialog for confirming the deletion of a file or folder.
     * 
     * @param id - The ID of the file or folder to be deleted.
     * @param type - The type of the item to be deleted (e.g., 'file' or 'folder').
     */
    public openDialog_trash(ids:string[], type): void {
        //Check permissions

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.trashForm).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.trash(ids, type);
            }
        });
    }

    /**
     * Opens a dialog for trashing multiple items.
     * 
     * @param items - An array of items to be trashed.
     */
    public openDialog_trash_multi(items: string[]): void {
        //Check permissions

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.trashMultiFiles).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);

            if (result === 'confirmed') {
                this.trashItem_multi(items);
            }
            
        });
    }

    /**
     * Stops the propagation of the given event.
     * 
     * @param event - The event to stop propagation for.
     */
    public stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    /**
     * Opens a dialog to restore an item.
     * 
     * @param item - The item to be restored.
     */
    openDialog_restore(id:string): void {
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.restoreForm).value);
        const ids:string[]=[id]
        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.docService.restore(ids).then((res)=>{
                    this.toastr.success(`Item successfully recovered from ${this.labelPipe.transform('Trash', 'trashLocationLabel')}`, "Restored");
                    this.eventSer.checkUnreadMessages.emit("Recovered");
                    this.eventSer.getTrashData.emit(true);
                }).catch((error)=>{
                    console.log(error);
                })
            }else console.log("Diallog closed");
        });
    }

    /**
     * Opens a dialog to restore multiple items.
     * 
     * @param items - The items to be restored.
     */
    openDialog_restore_multi(items: string[]): void {
        //Check permissions

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.restoreForm).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.restoreItems(items);
            }
        });
    }

     /**
     * Opens the delete all dialog and performs the necessary actions based on the user's confirmation.
     */
     public openDialog_delete_all(): void {
        //Check permissions

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(this._formBuilder.group(this.util.deleteAllForm).value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);

            if (result === 'confirmed') {
                this.emptyTrash(null);
            }
        });
    }

    /**
     * Opens a dialog to inform the user that the copilot parsing is still in progress.
     */
    public open_copilot_parse_info(type = 'folder'): void {
        this._fuseConfirmationService.open({
            message: `This ${type} is still being processed. Please try again later.`,
            title: 'Parsing Status',
            icon: { color: 'info', name: 'feather:info', show: true },
            actions: {
                confirm: {
                    show: false,

                },
                cancel: {
                    show: true,
                    label: 'Close'
                }
            },
        },
    )
    }

}
